import {
  DESKTOP_MIN_WIDTH_PX,
  resolveAutoDesktopMode,
  resolveDesktopDisplayMode,
  resolveForcePcPreference,
} from "@/shared/lib/forcePc";

export type PwaInstallPromptState = "unavailable" | "available" | "installed";

type PwaInstallPromptOutcome = "accepted" | "dismissed" | "unavailable";

type InstallPromptChoice = Readonly<{
  outcome: "accepted" | "dismissed";
}>;

type BeforeInstallPromptEventLike = Event & {
  prompt(): Promise<void | InstallPromptChoice>;
  userChoice: Promise<InstallPromptChoice>;
};

type InstallPromptEventTarget = {
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
};

type MediaQueryWindow = {
  matchMedia?(query: string): Pick<MediaQueryList, "matches">;
  location?: Pick<Location, "search">;
};

type CookieDocument = {
  cookie: string;
};

type StandaloneNavigator = {
  standalone?: boolean;
};

export type PwaInstallPromptStore = Readonly<{
  getState: () => PwaInstallPromptState;
  requestInstall: () => Promise<PwaInstallPromptOutcome>;
  startCapture: (target?: InstallPromptEventTarget) => () => void;
  stopCapture: () => void;
  subscribe: (listener: () => void) => () => void;
}>;

function isBeforeInstallPromptEvent(
  event: Event,
): event is BeforeInstallPromptEventLike {
  const candidate = event as {
    prompt?: unknown;
    userChoice?: { then?: unknown };
  };
  return (
    typeof candidate.prompt === "function" &&
    typeof candidate.userChoice?.then === "function"
  );
}

/**
 * `beforeinstallprompt` をUIが出るまで保持する小さな外部store。
 *
 * このeventは発火時刻が保証されず、Reactの初回描画より先に届く場合があるため、
 * `/mix` のentryで早期にcaptureしてからオンボーディングが購読する。
 */
export function createPwaInstallPromptStore(): PwaInstallPromptStore {
  let state: PwaInstallPromptState = "unavailable";
  let promptEvent: BeforeInstallPromptEventLike | null = null;
  let stopCurrentCapture: (() => void) | null = null;
  const listeners = new Set<() => void>();

  function notify(): void {
    for (const listener of listeners) listener();
  }

  function setState(nextState: PwaInstallPromptState): void {
    if (state === nextState) return;
    state = nextState;
    notify();
  }

  return {
    getState: () => state,
    requestInstall: async (): Promise<PwaInstallPromptOutcome> => {
      const capturedEvent = promptEvent;
      if (capturedEvent === null) return "unavailable";

      // BeforeInstallPromptEventは1回しか使えないため、呼び出す前に退避を破棄する。
      promptEvent = null;
      setState("unavailable");
      await capturedEvent.prompt();
      const result = await capturedEvent.userChoice;
      return result.outcome;
    },
    startCapture: (
      target: InstallPromptEventTarget = globalThis.window,
    ): (() => void) => {
      stopCurrentCapture?.();

      const handleBeforeInstallPrompt: EventListener = (event: Event): void => {
        if (!isBeforeInstallPromptEvent(event)) return;
        // ブラウザ任せの表示を抑え、板選択直後の案内ボタンから開けるようにする。
        event.preventDefault();
        promptEvent = event;
        setState("available");
      };
      const handleAppInstalled: EventListener = (): void => {
        promptEvent = null;
        setState("installed");
      };

      target.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      target.addEventListener("appinstalled", handleAppInstalled);

      const stopCapture = (): void => {
        target.removeEventListener(
          "beforeinstallprompt",
          handleBeforeInstallPrompt,
        );
        target.removeEventListener("appinstalled", handleAppInstalled);
        promptEvent = null;
        if (state !== "installed") setState("unavailable");
        if (stopCurrentCapture === stopCapture) stopCurrentCapture = null;
      };
      stopCurrentCapture = stopCapture;
      return stopCapture;
    },
    stopCapture: (): void => stopCurrentCapture?.(),
    subscribe: (listener: () => void): (() => void) => {
      listeners.add(listener);
      return (): void => {
        listeners.delete(listener);
      };
    },
  };
}

export const pwaInstallPromptStore = createPwaInstallPromptStore();

type PwaInstallCaptureConditions = Readonly<{
  isMixMount: boolean;
  isOnboardingRequired: boolean;
  isMobileDisplay: boolean;
  isInstalledPwa: boolean;
}>;

/** custom UIが必ず続く訪問だけで、ブラウザ既定のinstall UIを抑止する。 */
export function shouldCapturePwaInstallPrompt({
  isMixMount,
  isOnboardingRequired,
  isMobileDisplay,
  isInstalledPwa,
}: PwaInstallCaptureConditions): boolean {
  return (
    isMixMount && isOnboardingRequired && isMobileDisplay && !isInstalledPwa
  );
}

/** アプリ本体と同じPC/SP判定で、スマホ版表示だけをPWA案内の対象にする。 */
export function isMobilePwaTarget(
  windowLike: MediaQueryWindow | undefined = typeof window === "undefined"
    ? undefined
    : window,
  documentLike: CookieDocument | undefined = typeof document === "undefined"
    ? undefined
    : document,
): boolean {
  try {
    if (windowLike?.matchMedia === undefined) return false;
    const viewportIsDesktop = windowLike.matchMedia(
      `(min-width: ${DESKTOP_MIN_WIDTH_PX}px)`,
    ).matches;
    const forceMode = resolveForcePcPreference(
      windowLike.location?.search ?? "",
      documentLike?.cookie ?? "",
    ).mode;
    const displayMode = resolveDesktopDisplayMode(
      resolveAutoDesktopMode(viewportIsDesktop),
      forceMode,
    );
    return displayMode === "mobile" || displayMode === "forced-mobile";
  } catch {
    return false;
  }
}

/** ホーム画面からstandalone表示で起動中なら、重ねて追加を案内しない。 */
export function isRunningAsInstalledPwa(
  windowLike: MediaQueryWindow | undefined = typeof window === "undefined"
    ? undefined
    : window,
  navigatorLike: StandaloneNavigator | undefined = typeof navigator ===
  "undefined"
    ? undefined
    : (navigator as StandaloneNavigator),
): boolean {
  try {
    return (
      windowLike?.matchMedia?.("(display-mode: standalone)").matches === true ||
      navigatorLike?.standalone === true
    );
  } catch {
    return false;
  }
}
